import {AfterViewInit, ChangeDetectionStrategy, Component, ViewChild} from '@angular/core';
import {UserService} from "../../../../@shared/services/user.service";
import {combineLatest, Observable} from "rxjs";
import {IUserWallet} from "../services/interfaces/user-wallet.interface";
import {FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {WalletTypeIdEnum} from "../../../../@core/enums/wallet-type-id.enum";
import {WalletTypeEnum} from "../../../../@core/enums/wallet-type.enum";
import {AgentKeyEnum} from "../../../../@core/enums/agent-key.enum";
import {WalletTransactionTypeIdEnum} from "../../../../@core/enums/wallet-transaction-type-id.enum";
import {enumValues} from "../../../../@core/utils/enum.helper";
import {MatPaginator} from "@angular/material/paginator";
import {BalanceHistoryDataSource} from "./balance-history.data-source";
import {cleanObject} from "../../../../@core/utils/clearObject";
import {IBalanceHistoryBody} from "../services/interfaces/balance-history.interface";
import {WalletTypesService} from "../../../../@core/services/wallet-types.service";
import {IWalletTypeInfo} from "../../../../@core/interfaces/wallet-type-info.interface";
import {map} from "rxjs/operators";

interface IBalanceHistoryForm {
  walletTypes: FormControl<string[] | null>
  agentKeys: FormControl<string[] | null>
  typeIds: FormControl<number[] | null>
}


@Component({
  selector: 'app-balance-history',
  templateUrl: './balance-history.component.html',
  styleUrls: ['./balance-history.component.scss'],

  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BalanceHistoryComponent implements AfterViewInit {
  @ViewChild(MatPaginator, {static: false}) paginator!: MatPaginator;

  private appliedFilters: IBalanceHistoryBody = {
    walletTypes: [WalletTypeEnum.REAL],
  };

  public balanceHistoryColumns: string[] = ['wallet', 'agentKey', 'amount', 'type', 'data', 'createdAt'];
  public balanceHistorySource = new BalanceHistoryDataSource(this.userService);

  public userWallets$: Observable<IUserWallet[]> = this.userService.getUserWallets();
  public walletTypesById$: Observable<Map<number, IWalletTypeInfo>> = this.walletTypesService.getAll().pipe(
    map(list => new Map(list.map(item => [item.id, item])))
  );

  public walletOptions$: Observable<IUserWallet[]> = combineLatest([
    this.userWallets$,
    this.walletTypesById$,
  ]).pipe(
    map(([wallets, typesById]) => {
      const hasReal = wallets.some(w => w.walletType?.id === WalletTypeIdEnum.REAL);
      if (hasReal || !typesById.has(WalletTypeIdEnum.REAL)) {
        return wallets;
      }
      const realPlaceholder: IUserWallet = {
        id: 0,
        balance: 0,
        walletType: {id: WalletTypeIdEnum.REAL},
      };
      return [realPlaceholder, ...wallets];
    })
  );

  public balanceHistoryForm: FormGroup<IBalanceHistoryForm> = this.fb.group({
    walletTypes: this.fb.control<string[] | null>([WalletTypeEnum.REAL]),
    agentKeys: this.fb.control<string[] | null>(null),
    typeIds: this.fb.control<number[] | null>(null)
  })

  constructor(private userService: UserService,
              private walletTypesService: WalletTypesService,
              private fb: FormBuilder) {
    this.loadBalanceHistory();
  }

  ngAfterViewInit() {
    this.paginator.page.subscribe(() => this.loadBalanceHistory());
  }

  loadBalanceHistory(): void {
    this.balanceHistorySource.loadData(
      this.appliedFilters,
      {
        size: this.paginator?.pageSize || 100,
        number: this.paginator?.pageIndex + 1 || 1,
      }
    );
  }

  apply(): void {
    this.appliedFilters = cleanObject(this.balanceHistoryForm.value) as IBalanceHistoryBody;
    this.paginator.pageIndex = 0;
    this.loadBalanceHistory();
  }

  protected readonly WalletTypeIdEnum = WalletTypeIdEnum;
  protected readonly WalletTypeEnum = WalletTypeEnum;
  protected readonly AgentKeyEnum = AgentKeyEnum
  protected readonly WalletTransactionTypeIdEnum = WalletTransactionTypeIdEnum
  protected readonly enumValues = enumValues;
}
